// Add data types to window.navigator for use in this file. See https://www.typescriptlang.org/docs/handbook/triple-slash-directives.html#-reference-types- for more info.
/// <reference types="user-agent-data-types" />
import { cn } from "@/lib/utils";
import { AnimatedButton } from "components/animated/AnimatedButton";
import AnimatedSplitText from "components/animated/AnimatedSplitText";
import { Text } from "components/core/Text";
import { AppContext } from "contexts/AppContext";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "hooks/useAuth";
import { useExtensionInstalled } from "hooks/useExtensionInstalled";
import { useTheme } from "next-themes";
import Image from "next/image";
import {
    ClassAttributes,
    HTMLAttributes,
    useContext,
    useEffect,
    useState
} from "react";
import { EXTENSION_STORE_URLS } from "utils/enum";

const AnimatePresenceComponent = AnimatePresence as any;

export default function ChromeBanner({
    className,
    ...props
}: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>) {
    const { me } = useAuth();
    const { browserName, deviceType } = useContext(AppContext);
    const [isChromePC, setIsChromePC] = useState(false);
    const isExtensionInstalled = useExtensionInstalled();

    useEffect(() => {
        const isChromiumBased =
            typeof navigator !== "undefined" &&
            !!navigator.userAgentData &&
            navigator.userAgentData.brands.some(
                (data) => data.brand === "Chromium"
            );

        const shouldShowChromeExtension =
            isChromiumBased &&
            deviceType === "desktop" &&
            EXTENSION_STORE_URLS[browserName.toUpperCase()];

        setIsChromePC(!!shouldShowChromeExtension);
    }, [browserName, deviceType]);

    // Don't show banner if extension is already installed
    if (isExtensionInstalled === true) {
        return null;
    }

    return (
        <div
            className={cn("flex flex-col items-center gap-1 py-11", className)}
            {...props}
        >
            {me ? (
                <>
                    <AnimatedSplitText
                        text="새 탭에서 믹스의 콘텐츠를 만나보세요"
                        delay={30}
                        splitType="chars"
                        ease="power3.out"
                    />
                    <Text color="text.disabled" variant="body1" tag="p">
                        확장 프로그램 사용자는 한 주에 13개의 콘텐츠를 더 읽고
                        있어요.
                    </Text>
                </>
            ) : (
                <>
                    <AnimatedSplitText
                        text="마케터에게 필요한 모든 인사이트를 한 곳에"
                        delay={30}
                        splitType="chars"
                        ease="power3.out"
                    />
                    <Text color="text.disabled" variant="body1" tag="p">
                        믹스에서 최신 트렌드와 마케팅 인사이트를 발견하세요.
                    </Text>
                </>
            )}
            {isChromePC && (
                <div className="mt-8 flex gap-3">
                    <BannerLogoAnimation />
                    <AnimatedButton
                        firstContent={`${browserName}에 추가`}
                        secondaryContent="지금 설치하세요"
                        onClick={() => {
                            window.open(
                                EXTENSION_STORE_URLS[browserName.toUpperCase()]
                                    ?.url,
                                "_blank"
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
}

const BannerLogoAnimation = () => {
    const [logoIndex, setLogoIndex] = useState(0);
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === "dark";

    const logos: string[] = [
        isDark ? "/img/logo_animation_1_dark.png" : "/img/logo_animation_1.png", // 브런치
        "/img/logo_animation_2.png", // 미디엄
        isDark ? "/img/logo_animation_3_dark.png" : "/img/logo_animation_3.png", // 유튜브
        isDark ? "/img/logo_animation_4_dark.png" : "/img/logo_animation_4.png", // Think with google
        isDark ? "/img/logo_animation_5_dark.png" : "/img/logo_animation_5.png", // 네이버 블로그
        "/img/logo_animation_6.png" // 메일리
    ];

    const INTERVAL_TIME = 1305;
    useEffect(() => {
        const timer = setInterval(() => {
            setLogoIndex((prev) => (prev + 1) % logos.length);
        }, INTERVAL_TIME);

        return () => clearInterval(timer);
    }, []);

    const MotionDiv = motion.div as any;

    return (
        <AnimatePresenceComponent mode="wait">
            <MotionDiv
                key={logoIndex}
                initial={{ opacity: 0, x: -15, scale: 0.5 }}
                animate={{
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: { duration: 0.3 }
                }}
                exit={{
                    opacity: 0,
                    x: 20,
                    scale: 0.1,
                    transition: { duration: 0.25 }
                }}
                transition={{ delay: 0.35 }}
            >
                <Image
                    className="h-12 w-12 rounded-2xl"
                    src={logos[logoIndex]!}
                    alt="logo"
                    width={48}
                    height={48}
                />
            </MotionDiv>
        </AnimatePresenceComponent>
    );
};
